import { TranslationLabels } from '@generated/translation-labels';
import Hidden from '@material-ui/core/Hidden';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell, { TableCellProps } from '@material-ui/core/TableCell';
import TablePagination from '@material-ui/core/TablePagination';
import TableContainer from '@material-ui/core/TableContainer';
import { ClassNameMap } from '@material-ui/styles';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Typography from '@material-ui/core/Typography';
import LinearProgress from '@material-ui/core/LinearProgress';
import { Box, Spinner } from '@shared/components';
import * as Sentry from '@sentry/react';
import clsx from 'clsx';
import { UseQueryResult } from 'react-query';
import React, { ChangeEvent, FC, MouseEvent, ReactNode, useState } from 'react';
import { useHistory } from 'react-router';
import { useTranslation } from '@shared/translations';
import { generateUniqId } from '../../helpers';
import { ArrowRightIcon } from '../icons';
import { useStyles } from './list.styles';

export type ListApiPaginationModel<T> = {
  getContent: (element: T) => string | ReactNode;
  id: string;
  label: string;
  align?: TableCellProps['align'];
  disableSorting?: boolean;
  width?: TableCellProps['width'];
};

export type ListApiPaginationProps<T> = {
  model: ListApiPaginationModel<T>[];
  to: GenericTypes.URL | ((element: T) => void);
  className?: string;
  isWidget?: boolean;
  defaultPageSize: number;
  pageSizeOptions: number[];
  paginatedQuery: (
    pagination: GenericTypes.Pagination,
    sort?: GenericTypes.Sort<string>,
  ) => UseQueryResult<GenericTypes.Paginated<T>>;
  defaultSortBy: string;
  classes?: ClassNameMap<string>;
};

export function ListApiPagination<T>(
  props: ListApiPaginationProps<T>,
): ReturnType<FC<ListApiPaginationProps<T>>> {
  const ownClasses = useStyles();
  const history = useHistory();
  const { t } = useTranslation();
  const {
    className,
    isWidget = false,
    model,
    to,
    defaultPageSize,
    pageSizeOptions,
    paginatedQuery,
    defaultSortBy,
    classes: overridenClasses,
  } = props;

  const classes = overridenClasses || ownClasses;
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [order, setOrder] = useState<GenericTypes.Sort<string>['order']>('asc');
  const [sortBy, setSortBy] = useState<GenericTypes.Sort<string>['sortBy']>(
    defaultSortBy,
  );

  const {
    isFetching,
    isLoading,
    isError,
    error,
    data: paginatedData,
  } = paginatedQuery(
    {
      page: page + 1,
      pageSize,
    },
    { order, sortBy },
  );

  const tableClassNames = [
    classes.table,
    ...(className ? [className] : []),
  ].join(' ');
  const isHead = model.filter(({ label }) => label).length > 0;

  const handlePageChange = (
    event: MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ): void => {
    setPage(newPage);
  };

  const handleLimitChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setPageSize(Number(event.target.value));
  };

  const handleSort = (id: string) => (): void => {
    const isActive = sortBy === id;

    if (isActive) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
      return;
    }

    setSortBy(id);
    setOrder('asc');
  };

  const handleRowClick = async (element: T): Promise<void> => {
    if (typeof to === 'string') {
      history.push({
        pathname: `${to}`,
      });
    } else if (typeof to === 'function') {
      history.push({
        pathname: `${to(element)}`,
      });
    }
  };

  if (isLoading) {
    return <Spinner />;
  }

  if (isError) {
    Sentry.captureException(error);
    return null;
  }

  return (
    <TableContainer className={classes.container}>
      <Table className={tableClassNames}>
        <TableBody>
          {isHead && (
            <Hidden xsDown>
              <TableRow>
                {model.map(({ align, disableSorting, id, label, width }) => (
                  <TableCell
                    align={align}
                    className={classes.headCell}
                    key={generateUniqId()}
                    sortDirection={order}
                    width={width}
                  >
                    {disableSorting ? (
                      t(label)
                    ) : (
                      <TableSortLabel
                        active={sortBy === id}
                        data-property={id}
                        direction={order}
                        onClick={handleSort(id)}
                      >
                        {t(label)}
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
                <TableCell
                  className={clsx(classes.headCell, classes.actionCell)}
                  align="right"
                  width="60"
                />
              </TableRow>
            </Hidden>
          )}
          {paginatedData?.results && paginatedData?.results.length > 0 ? (
            paginatedData?.results.map((element) => (
              <TableRow
                className={classes.row}
                key={generateUniqId()}
                hover
                onClick={() => {
                  handleRowClick(element);
                }}
              >
                {model.map(({ align, getContent, label, width }) => (
                  <TableCell
                    align={align}
                    className={classes.cell}
                    key={generateUniqId()}
                    style={{
                      width: `${width}px`,
                    }}
                  >
                    {label && (
                      <Hidden smUp>
                        <Typography className={classes.cellLabel}>
                          <strong>{t(label)}:</strong>
                        </Typography>
                      </Hidden>
                    )}
                    {getContent(element)}
                  </TableCell>
                ))}
                <Hidden xsDown>
                  <TableCell align="right" className={classes.cell} width={60}>
                    <ArrowRightIcon width={24} height={24} />
                  </TableCell>
                </Hidden>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                className={classes.emptyCell}
                colSpan={model.length + 1}
              >
                <Typography>
                  <strong>{t(TranslationLabels.listEmptyText)}</strong>
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {!isWidget && (
        <Box className={classes.pagination}>
          <TablePagination
            component="div"
            count={paginatedData?.count || 0}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handleLimitChange}
            page={page}
            rowsPerPage={pageSize}
            rowsPerPageOptions={pageSizeOptions}
            labelRowsPerPage={t(TranslationLabels.rowsPerPageLabel)}
            classes={{
              actions: classes.paginationActions,
              root: classes.paginationRoot,
              selectRoot: classes.paginationActions,
              toolbar: classes.paginationToolbar,
            }}
          />
        </Box>
      )}
      {isFetching && <LinearProgress />}
    </TableContainer>
  );
}
